/*
 * Project: Goalscape
 * File:    unsplashUtils.js
 * Path:    src/utils/unsplashUtils.js
 * Description: Utility functions for interacting with the Unsplash API.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement caching for Unsplash API responses to reduce network requests and improve performance.
 */

/*
 * Function: fetchUnsplashImages
 * Description: Fetches random images from Unsplash based on a search query.
 * Parameters:
 *   query: string - The search term for images.
 *   count: number - Number of images to fetch (default: 20).
 * Returns: Promise<Array> - Array of image objects from Unsplash.
 * Usage:
 *   const images = await fetchUnsplashImages('nature', 10);
 * AI Hint: Implement lazy loading for images to improve initial page load time.
 */
const fetchUnsplashImages = async (query, count = 20) => {
    const response = await fetch(
        `https://api.unsplash.com/photos/random?count=${count}&query=${query}&orientation=landscape`,
        {
            headers: {
                'Authorization': `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`
            }
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch images');
    }

    return response.json();
};

export { fetchUnsplashImages };
